import {
  ProblemJSEnvironmentConfig,
  redirectToMaintenancePageUrlIfNeeded,
} from '@gmm/problem';

interface EnvironmentConfig extends ProblemJSEnvironmentConfig {
  NAMESPACE: string;
  STUDENT_GIT_SHA: string;
}

let config: EnvironmentConfig;

const FALLBACK_CONFIG: EnvironmentConfig = {
  NAMESPACE: process.env.NAMESPACE || 'dev',
  JAVA_URL: process.env.JAVA_URL || 'http://host.docker.internal:8080',
  STUDENT_GIT_SHA: process.env.STUDENT_GIT_SHA || '1234',
  ROBOTO_GMM: process.env.ROBOTO_GMM || 'default_value',
};

// Initialize config by fetching config.json
export async function initConfig(): Promise<void> {
  try {
    // Query param to prevent caching
    const response = await fetch(`/config.json?t=${Date.now()}`);

    if (redirectToMaintenancePageUrlIfNeeded(response.url)) {
      return waitForMaintenanceNavigation();
    }

    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status}`);
    }

    const loadedConfig = await response.json();

    // Validate all required fields are present
    const requiredFields: Array<keyof EnvironmentConfig> = [
      'NAMESPACE',
      'JAVA_URL',
      'STUDENT_GIT_SHA',
      'FONT_URL',
    ];

    for (const field of requiredFields) {
      if (!(field in loadedConfig) || loadedConfig[field] === null) {
        throw new Error(`Missing or null required config field: ${field}`);
      }
    }

    config = loadedConfig as EnvironmentConfig;
  } catch (error) {
    // Fallback defaults from build or for dev or fetch failure
    config = FALLBACK_CONFIG;
  }
}

// Keeps startup from rendering with local-dev config while maintenance navigation replaces the page.
const waitForMaintenanceNavigation = (): Promise<void> =>
  new Promise<void>(() => {});

export function getConfig(): EnvironmentConfig {
  if (!config) {
    console.warn('Config not initialized');

    return FALLBACK_CONFIG;
  }

  return config;
}

export async function getConfigAsync(): Promise<EnvironmentConfig> {
  if (!config) {
    await initConfig();
  }

  return config;
}
